/**
 * Atlas API client — thin fetch wrapper with Bearer token + envelope unwrapping.
 *
 * Backend envelope:
 *   Success: { data, meta? }
 *   Error:   { error: { code, message, details? } }
 */

const API_BASE = '/api/v1'
const TOKEN_KEY = 'atlas_token'

export class ApiError extends Error {
  code: string
  status: number
  details?: Record<string, string[]>
  constructor(code: string, message: string, status: number, details?: Record<string, string[]>) {
    super(message)
    this.code = code
    this.status = status
    this.details = details
  }
}

export const tokenStore = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string): void => { localStorage.setItem(TOKEN_KEY, token) },
  clear: (): void => { localStorage.removeItem(TOKEN_KEY) },
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  /** When true, do not attach Authorization even if a token exists. */
  anonymous?: boolean
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, anonymous = false } = opts

  const url = new URL(API_BASE + path, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v))
    }
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = tokenStore.get()
  if (token && !anonymous) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  })

  // 204 No Content
  if (res.status === 204) return undefined as T

  let payload: unknown
  try {
    payload = await res.json()
  } catch {
    throw new ApiError('parse_error', `Failed to parse response (${res.status})`, res.status)
  }

  if (!res.ok) {
    const err = (payload as { error?: { code: string; message: string; details?: Record<string, string[]> } }).error
    if (err) {
      // Auto-clear token on 401
      if (res.status === 401) tokenStore.clear()
      throw new ApiError(err.code, err.message, res.status, err.details)
    }
    throw new ApiError('http_error', `HTTP ${res.status}`, res.status)
  }

  // Unwrap { data } envelope
  return (payload as { data: T }).data
}

export type UploadProgress = { loaded: number; total: number }

/**
 * Multipart upload.
 *
 * Separate from api() for two reasons: that wrapper always sets a JSON
 * Content-Type and stringifies the body, and fetch() cannot report upload
 * progress at all — only XHR exposes `upload.onprogress`, which is the
 * difference between a progress bar and a spinner on a 20MB photo.
 *
 * Deliberately does NOT set Content-Type: the browser has to write it itself so
 * it can include the multipart boundary.
 *
 * (listStories() already drops to a raw fetch to keep pagination meta, so this
 * is the second documented exception rather than a new pattern.)
 */
export function apiUpload<T>(
  path: string,
  form: FormData,
  opts: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', API_BASE + path)

    const token = tokenStore.get()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Accept', 'application/json')

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.({ loaded: e.loaded, total: e.total })
    }

    xhr.onload = () => {
      let payload: unknown
      try {
        payload = JSON.parse(xhr.responseText)
      } catch {
        reject(new ApiError('parse_error', `Failed to parse response (${xhr.status})`, xhr.status))
        return
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve((payload as { data: T }).data)
        return
      }
      if (xhr.status === 401) tokenStore.clear()
      const err = (payload as { error?: { code: string; message: string; details?: Record<string, string[]> } }).error
      reject(err
        ? new ApiError(err.code, err.message, xhr.status, err.details)
        : new ApiError('http_error', `HTTP ${xhr.status}`, xhr.status))
    }

    xhr.onerror = () => reject(new ApiError('network_error', 'Network error during upload', 0))
    xhr.onabort = () => reject(new ApiError('aborted', 'Upload cancelled', 0))
    opts.signal?.addEventListener('abort', () => xhr.abort())

    xhr.send(form)
  })
}
